import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import {
  InsightsApiClient,
  GetRealTimeLogsRequest,
  GetRealTimeLogsResult,
  BaseRequestPayload
} from '../../api/api-client';
import { ResultValue } from '@@/types/ApiResult';

export const useGetRealTimeLogs = <T extends GetRealTimeLogsRequest['kind']>(
  parameters: Omit<Extract<GetRealTimeLogsRequest, { kind: T }>, 'instanceAlias'>
) => {
  const { instanceData } = useContext(AuthContext);

  const fetchLog = async (): Promise<GetRealTimeLogsResult<T>> => {
    if (!instanceData) {
      throw new Error('AppContext and AuthContext must be initialized.');
    }

    const client = new InsightsApiClient();
    const payload: BaseRequestPayload & Extract<GetRealTimeLogsRequest, { kind: T }> = {
      ...parameters,
      instanceId: instanceData.instanceId,
      region: instanceData.region,
      instanceAlias: instanceData.instanceAlias
    } as BaseRequestPayload & Extract<GetRealTimeLogsRequest, { kind: T }>;

    const result = await client.getRealTimeLogs(payload);

    if (!ResultValue.ok(result)) {
      throw new Error(result?.message || 'Failed to fetch real-time logs');
    }

    return result.data;
  };

  const {
    data,
    isLoading,
    error: apiError,
    isRefetching,
    refetch
  } = useQuery<GetRealTimeLogsResult<T>, Error>({
    queryKey: ['log', instanceData?.instanceId, instanceData?.region, parameters.contactId, parameters.kind],
    queryFn: fetchLog,
    enabled: !!(instanceData?.instanceId && instanceData?.region && parameters.contactId && parameters.kind),
    refetchOnWindowFocus: false,
    staleTime: 3000,
    gcTime: 3000,
    retry: 0
  });

  return {
    data,
    isLoading,
    isRefetching,
    error: apiError?.message ?? null,
    refetch
  };
};
